import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { notificationsApi } from '../../services/apiService';
import './AdminNotificationPage.css';

const AdminNotificationPage = () => {
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [targetType, setTargetType] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // User search for targeting
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserLabel, setSelectedUserLabel] = useState('');

  // Sent notifications history
  const [sentNotifications, setSentNotifications] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Fetch sent notifications
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const data = await notificationsApi.adminGetAll();
      setSentNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to load notification history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // User search debounce
  useEffect(() => {
    if (targetType !== 'specific' || userSearch.length < 2) {
      setUserResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const data = await notificationsApi.getUsers(userSearch);
        setUserResults(data.users || []);
        setShowUserDropdown(true);
      } catch (err) {
        console.error('User search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, targetType]);

  const handleSelectUser = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserLabel(`${user.firstName || ''} ${user.lastName || ''} (${user.email})`.trim());
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!title.trim()) { setFormError('Title is required'); return; }
    if (!message.trim()) { setFormError('Message is required'); return; }
    if (targetType === 'specific' && !selectedUserId) { setFormError('Please select a user'); return; }

    try {
      setSending(true);
      await notificationsApi.create({
        title: title.trim(),
        message: message.trim(),
        type,
        targetType,
        userId: targetType === 'specific' ? selectedUserId : undefined
      });
      setFormSuccess(`Notification sent to ${targetType === 'all' ? 'all users' : selectedUserLabel}!`);
      setTitle('');
      setMessage('');
      setType('info');
      setTargetType('all');
      setSelectedUserId('');
      setSelectedUserLabel('');
      fetchHistory();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification for all recipients?')) return;
    try {
      await notificationsApi.adminDelete(id);
      setSentNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      alert('Failed to delete notification');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const typeBadgeClass = (t) => `anp-type-badge anp-type-${t}`;

  return (
    <AdminLayout>
      <div className="admin-notification-content">
        {/* Header */}
        <div className="anp-page-header">
          <h1 className="anp-page-title">Notifications</h1>
          <p className="anp-page-subtitle">Send notifications to users</p>
        </div>

        {/* Send Form */}
        <div className="anp-form-card">
          <h2 className="anp-section-title">Create Notification</h2>

          {formError && <div className="anp-alert anp-alert-error">{formError}</div>}
          {formSuccess && <div className="anp-alert anp-alert-success">{formSuccess}</div>}

          <form onSubmit={handleSend} className="anp-form">
            <div className="anp-form-row">
              <div className="anp-field">
                <label>Title <span className="anp-req">*</span></label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Notification title" maxLength={200} />
              </div>
              <div className="anp-field anp-field-sm">
                <label>Type</label>
                <select value={type} onChange={e => setType(e.target.value)}>
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                </select>
              </div>
            </div>

            <div className="anp-field">
              <label>Message <span className="anp-req">*</span></label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Write the notification message..." rows={4} maxLength={2000} />
              <span className="anp-char-count">{message.length}/2000</span>
            </div>

            <div className="anp-form-row">
              <div className="anp-field">
                <label>Send To</label>
                <div className="anp-radio-group">
                  <label className={`anp-radio-option ${targetType === 'all' ? 'active' : ''}`}>
                    <input type="radio" name="targetType" value="all" checked={targetType === 'all'} onChange={() => { setTargetType('all'); setSelectedUserId(''); setSelectedUserLabel(''); }} />
                    <span className="anp-radio-label">All Users</span>
                  </label>
                  <label className={`anp-radio-option ${targetType === 'specific' ? 'active' : ''}`}>
                    <input type="radio" name="targetType" value="specific" checked={targetType === 'specific'} onChange={() => setTargetType('specific')} />
                    <span className="anp-radio-label">Specific User</span>
                  </label>
                </div>
              </div>

              {targetType === 'specific' && (
                <div className="anp-field anp-user-search-field">
                  <label>Select User <span className="anp-req">*</span></label>
                  {selectedUserLabel ? (
                    <div className="anp-selected-user">
                      <span>{selectedUserLabel}</span>
                      <button type="button" onClick={() => { setSelectedUserId(''); setSelectedUserLabel(''); }}>×</button>
                    </div>
                  ) : (
                    <div className="anp-user-search-wrapper">
                      <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name or email..." />
                      {searchLoading && <span className="anp-search-spinner">...</span>}
                      {showUserDropdown && userResults.length > 0 && (
                        <div className="anp-user-dropdown">
                          {userResults.map(u => (
                            <div key={u.id} className="anp-user-option" onClick={() => handleSelectUser(u)}>
                              <span className="anp-user-name">{u.firstName || ''} {u.lastName || ''}</span>
                              <span className="anp-user-email">{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {showUserDropdown && userResults.length === 0 && userSearch.length >= 2 && !searchLoading && (
                        <div className="anp-user-dropdown"><div className="anp-user-option anp-no-result">No users found</div></div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="anp-send-btn" disabled={sending}>
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>

        {/* Sent History */}
        <div className="anp-history-card">
          <h2 className="anp-section-title">Sent Notifications</h2>
          {historyLoading ? (
            <div className="anp-loading">Loading...</div>
          ) : sentNotifications.length === 0 ? (
            <div className="anp-empty">No notifications sent yet.</div>
          ) : (
            <div className="anp-history-list">
              {sentNotifications.map(n => (
                <div key={n.id} className="anp-history-item">
                  <div className="anp-history-main">
                    <div className="anp-history-top">
                      <span className={typeBadgeClass(n.type)}>{n.type}</span>
                      <span className="anp-history-target">{n.recipientLabel || (n.targetType === 'all' ? 'All Users' : `User #${n.userId}`)}</span>
                      <span className="anp-history-date">{formatDate(n.createdAt)}</span>
                    </div>
                    <h3 className="anp-history-title">{n.title}</h3>
                    <p className="anp-history-message">{n.message}</p>
                  </div>
                  <button className="anp-delete-btn" onClick={() => handleDelete(n.id)} title="Delete">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNotificationPage;
